import {
  Button,
  ButtonGroup,
  Card,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  isCurrency,
} from 'platform/components';
import {Grid, HStack, VStack} from 'platform/foundation';

import {useGetTenantQuery} from '@dms/api/core';
import i18n from '@dms/i18n';

import {TestIdProps, suffixTestId} from 'shared/web';

import {PaymentSettings, paymentSettingsFormSchema} from '../types/paymentSettingsFormSchema';
import {IN_EUROPE, OUT_OF_EUROPE} from '../utils/const';

interface PaymentSettingsFormProps extends TestIdProps {
  canEditBankTransferSettings: boolean;
  defaultValues: Partial<PaymentSettings>;
  onSubmit: FormSubmitHandler<PaymentSettings>;
  onClose: VoidFunction;
}

export function PaymentSettingsForm(props: PaymentSettingsFormProps) {
  const abroadSaleOptions = [
    {label: i18n.t('entity.customerPaymentSettings.labels.saleToEU'), value: IN_EUROPE},
    {label: i18n.t('entity.customerPaymentSettings.labels.saleOutsideEU'), value: OUT_OF_EUROPE},
  ];

  const {data: tenant} = useGetTenantQuery();

  return (
    <Form<PaymentSettings>
      defaultValues={props.defaultValues}
      onSubmit={props.onSubmit}
      experimentalZodSchema={paymentSettingsFormSchema(props.canEditBankTransferSettings)}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <Card
            variant="inlineGrey"
            title={i18n.t('entity.customerPaymentSettings.labels.abroadSale')}
            control={{
              type: 'switch',
              value: formApi.watch('allowAbroadSale') ?? false,
              onChange: (value) => formApi.setValue('allowAbroadSale', value),
              'data-testid': suffixTestId('allowAbroadSale', props),
            }}
            isExpanded={formApi.watch('allowAbroadSale') ?? false}
          >
            <HStack spacing={4}>
              <FormField
                control={control}
                type="chips"
                name="abroadSale"
                isRequired
                options={abroadSaleOptions}
                data-testid={suffixTestId('abroadSaleOptions', props)}
              />
            </HStack>
          </Card>
          <Card
            variant="inlineGrey"
            title={i18n.t('entity.customerPaymentSettings.labels.allowBankTransfer')}
            control={{
              type: 'switch',
              value: formApi.watch('allowBankTransfer'),
              onChange: (value) => formApi.setValue('allowBankTransfer', value),
              isDisabled: !props.canEditBankTransferSettings,
              'data-testid': suffixTestId('allowBankTransfer', props),
            }}
            isExpanded={formApi.watch('allowBankTransfer')}
          >
            <VStack spacing={4}>
              <Grid spacing={4} columns={2}>
                <FormField
                  control={control}
                  type="integer"
                  name="invoiceMaturity"
                  label={i18n.t('entity.customerPaymentSettings.labels.invoiceMaturityDays')}
                  isRequired
                  data-testid={suffixTestId('invoiceMaturity', props)}
                  isDisabled={!props.canEditBankTransferSettings}
                />
                <FormField
                  control={control}
                  type="currency"
                  name="paymentLimit"
                  currency={isCurrency(tenant?.currency) ? tenant?.currency : undefined}
                  decimalPlaces={3}
                  label={i18n.t('entity.customerPaymentSettings.labels.paymentLimit')}
                  data-testid={suffixTestId('paymentLimit', props)}
                  isDisabled={!props.canEditBankTransferSettings}
                />
              </Grid>
            </VStack>
          </Card>
          <Card
            variant="inlineGrey"
            title={i18n.t('entity.customerPaymentSettings.labels.allowElectronicInvoiceEmail')}
            control={{
              type: 'switch',
              value: formApi.watch('allowElectronicInvoiceEmail'),
              onChange: (value) => formApi.setValue('allowElectronicInvoiceEmail', value),
              isDisabled: !props.canEditBankTransferSettings,
              'data-testid': suffixTestId('allowElectronicInvoiceEmail', props),
            }}
            isExpanded={formApi.watch('allowElectronicInvoiceEmail')}
          >
            <VStack spacing={4}>
              <FormField
                control={control}
                type="email"
                name="email"
                isRequired
                label={i18n.t('entity.customerPaymentSettings.labels.email')}
                data-testid={suffixTestId('email', props)}
                isDisabled={!props.canEditBankTransferSettings}
              />
            </VStack>
          </Card>
          <ButtonGroup align="right">
            <Button
              data-testid={suffixTestId('discard', props)}
              variant="secondary"
              onClick={props.onClose}
              title={i18n.t('general.actions.discard')}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('general.actions.save')}
              data-testid={suffixTestId('submit', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
